import { DataType, FormulaOperator } from '@doss/prisma/types'
import _ from 'lodash'
import * as yup from 'yup'
import type { FormulaValueInput } from '../antlr/utils/formula.js'
import { DossError } from './doss-error.js'
import {
  dataTypeToFormulaValueType,
  FormulaValueType,
  getFormulaOperatorSpec,
  getFormulaOutputType,
  isFormulaType,
  type FormulaType,
} from './formula-operator-specs.js'
import { FormulaJson } from './types/formula.js'

// The formula-type vocabulary (formulaTypes/isFormulaType/getFormulaOutputType) lives in
// formula-operator-specs.ts — the pure module the `formula-lang` subpath re-exports — and is
// re-exported here so every existing call site keeps its import.
export {
  formulaTypes,
  getFormulaOutputType,
  isFormulaType,
  type FormulaType,
} from './formula-operator-specs.js'

/**
 * The FORMULA_* column subtype for a formula with the given resolved output type.
 */
export function getFormulaColumnType(outputType: FormulaValueType): FormulaType {
  switch (outputType) {
    case FormulaValueType.NUMBER:
      return DataType.FORMULA_NUMBER
    case FormulaValueType.STRING:
      return DataType.FORMULA_STRING
    case FormulaValueType.DATE:
      return DataType.FORMULA_DATE
    case FormulaValueType.DATE_TIME:
      return DataType.FORMULA_DATE_TIME
  }
}

/**
 * Creation-time guard: the FORMULA_* column type assigned to the column must correspond to the
 * formula's backend-inferred output type.
 */
export function assertFormulaOutputMatchesColumnType(
  columnType: DataType,
  formulaOutputType: FormulaValueType | undefined,
): void {
  if (formulaOutputType === undefined || !isFormulaType(columnType)) {
    return
  }
  if (getFormulaOutputType(columnType) !== formulaOutputType) {
    throw new DossError('Formula output type %s does not match column type %s', [
      formulaOutputType,
      columnType,
    ])
  }
}

/**
 * Whether a formula value node carries no content — no column/form-input reference, no nested
 * formula, no conditions, and no scalar literal.
 */
export const isEmptyFormulaValue = (value: FormulaValueInput): boolean =>
  value.tableColumnId == null &&
  value.formInputId == null &&
  value.formula == null &&
  (value.conditions == null || value.conditions.length === 0) &&
  value.value == null &&
  value.valueNumber == null &&
  value.valueString == null

/**
 * Formulas can be made up of the following column types:
 * 1. Numbers (numeric formulas)
 * 2. Text-like scalars — STRING, ENUM (select), FILE_URL (file), USER, ID. Each resolves to a
 *    string, so they are valid operands for string functions.
 * 3. Date / DateTime (date formulas with date functions)
 * 4. Formulas (formulas that resolve to any of the above types)
 * 5. Lookups that resolve to any of the above types. Lookups aren't mentioned explicitly in this list
 *    because they are a valid column type for the formula input, but wherever this is used, we instead
 *    check the resolved type of the lookup column rather than the lookup itself.
 */
export const validFormulaColumnTypes: DataType[] = [
  DataType.NUMBER,
  DataType.FORMULA_NUMBER,
  DataType.STRING,
  DataType.FORMULA_STRING,
  DataType.ENUM,
  DataType.FILE_URL,
  DataType.USER,
  DataType.ID,
  DataType.DATE,
  DataType.FORMULA_DATE,
  DataType.DATE_TIME,
  DataType.FORMULA_DATE_TIME,
]

/**
 * The subset of `validFormulaColumnTypes` that resolves to a number. Column-level aggregation
 * operators (SUM, MIN, MAX, ...) only operate on numeric inputs, so their aggregated column must
 * resolve to one of these — this is intentionally narrower than `validFormulaColumnTypes`, which
 * also allows string/date columns as operands to row-level text/date functions.
 */
export const numericFormulaColumnTypes: DataType[] = [DataType.NUMBER, DataType.FORMULA_NUMBER]

/**
 * Date-only (not date & time) column types, for ADDITION/SUBTRACTION operands — these operators
 * are overloaded for date math (see `resolveBinaryArithmetic`) in addition to plain numbers.
 * DATE_TIME is intentionally excluded: `resolveBinaryArithmetic` rejects it in arithmetic,
 * directing users to DATEADD/DATEDIFF instead, which take an explicit calendar unit.
 */
export const dateFormulaColumnTypes: DataType[] = [DataType.DATE, DataType.FORMULA_DATE]

/**
 * The column type fields a referencing formula's generated SQL depends on.
 *
 * `lookupColumnType`/`lookupColumnInnerType` describe the column a LOOKUP resolves to, and must be
 * the *leaf* of the lookup chain — a lookup pointing at another lookup has to be followed to the
 * first non-lookup column, since only that column's type reaches the formula. `lookupColumnInnerType`
 * carries the leaf's element type so a lookup onto an array keeps it.
 */
export interface FormulaVisibleColumnFields {
  type: DataType
  innerType: DataType | null
  lookupColumnType?: DataType | null
  lookupColumnInnerType?: DataType | null
}

/**
 * What a formula observes for a column: a resolved scalar type, plus whether it arrives as an
 * array.
 */
export interface FormulaVisibleShape {
  isArray: boolean
  resolvedType: FormulaValueType | undefined
}

/**
 * The shape of the column a LOOKUP resolves to. Arrayness comes from the leaf's own type, so a
 * lookup onto an ARRAY column reads as an array of the leaf's element type.
 */
function getLookupTargetShape(column: FormulaVisibleColumnFields): FormulaVisibleShape {
  const { lookupColumnType, lookupColumnInnerType } = column

  if (lookupColumnType === null || lookupColumnType === undefined) {
    return { isArray: false, resolvedType: undefined }
  }

  if (lookupColumnType === DataType.ARRAY) {
    return {
      isArray: true,
      resolvedType:
        lookupColumnInnerType === null || lookupColumnInnerType === undefined
          ? undefined
          : dataTypeToFormulaValueType(lookupColumnInnerType),
    }
  }

  return { isArray: false, resolvedType: dataTypeToFormulaValueType(lookupColumnType) }
}

/**
 * The shape a formula sees for a column.
 *
 * `dataTypeToFormulaValueType` only inspects the type it is handed and has no ARRAY case, so every
 * array column resolves to `undefined` regardless of its element type. Passing an ARRAY column's
 * `type` would therefore make ARRAY<NUMBER> and ARRAY<STRING> compare equal. Array columns instead
 * resolve their `innerType`, and arrayness is recorded separately in `isArray` — two fields,
 * because one value cannot distinguish NUMBER from ARRAY<NUMBER> as well as ARRAY<NUMBER> from
 * ARRAY<STRING>.
 *
 * A lookup resolves through `lookupColumnType`/`lookupColumnInnerType` instead of its own type.
 * ARRAY<LOOKUP> onto an array leaf flattens to a single array — a formula receives one array of the
 * leaf's element type either way, so nesting is not tracked.
 */
export function getFormulaVisibleShape(column: FormulaVisibleColumnFields): FormulaVisibleShape {
  if (column.type === DataType.ARRAY) {
    if (column.innerType === DataType.LOOKUP) {
      return { isArray: true, resolvedType: getLookupTargetShape(column).resolvedType }
    }

    return {
      isArray: true,
      resolvedType:
        column.innerType === null ? undefined : dataTypeToFormulaValueType(column.innerType),
    }
  }

  if (column.type === DataType.LOOKUP) {
    return getLookupTargetShape(column)
  }

  return { isArray: false, resolvedType: dataTypeToFormulaValueType(column.type) }
}

/**
 * Whether two already-resolved shapes differ, and so whether a column type change alters what a
 * referencing formula observes.
 *
 * This is deliberately narrower than a raw type comparison. A formula reads a column's *resolved*
 * type, so changes that preserve it are invisible: FORMULA_NUMBER -> NUMBER (detaching a formula to
 * a static column) and ENUM -> STRING both keep the same operand type and cannot break a formula.
 * Changes that alter it break formulas permanently — FORMULA_NUMBER -> STRING makes numeric
 * aggregation emit `COALESCE(<text>, 0)`, and ENUM -> ARRAY<ENUM> makes a CONTAINS condition
 * compare an array against a scalar.
 */
export function isFormulaVisibleShapeChanged(
  oldShape: FormulaVisibleShape,
  newShape: FormulaVisibleShape,
): boolean {
  return oldShape.isArray !== newShape.isArray || oldShape.resolvedType !== newShape.resolvedType
}

export const columnFormulaOperators = [
  FormulaOperator.SUM,
  FormulaOperator.COUNT,
  FormulaOperator.COUNT_DISTINCT,
  FormulaOperator.AVERAGE,
  FormulaOperator.SUMPRODUCT,
  FormulaOperator.MIN,
  FormulaOperator.MAX,
]
export type ColumnFormulaOperator = (typeof columnFormulaOperators)[number]

/**
 * Row-level math operators (plus MIN/MAX/AVERAGE). Separate from text/date operators so
 * `rowFormulaOperators` can compose this list. Operand type rules — including MIN/MAX
 * accepting dates — live in `formula-operator-specs.ts`.
 */
export const arithmeticRowFormulaOperators = [
  FormulaOperator.ADDITION,
  FormulaOperator.MULTIPLICATION,
  FormulaOperator.SUBTRACTION,
  FormulaOperator.DIVISION,
  FormulaOperator.ROUND,
  FormulaOperator.CEIL,
  FormulaOperator.FLOOR,
  FormulaOperator.MIN,
  FormulaOperator.MAX,
  FormulaOperator.AVERAGE,
]
export const rowFormulaOperators = [
  ...arithmeticRowFormulaOperators,
  FormulaOperator.UPPER,
  FormulaOperator.LOWER,
  FormulaOperator.TRIM,
  FormulaOperator.LEN,
  FormulaOperator.CONCAT,
  FormulaOperator.LEFT,
  FormulaOperator.RIGHT,
  FormulaOperator.MID,
  FormulaOperator.SUBSTITUTE,
  FormulaOperator.DATE,
  FormulaOperator.DATEADD,
  FormulaOperator.DATEDIFF,
  FormulaOperator.YEAR,
  FormulaOperator.MONTH,
  FormulaOperator.DAY,
  FormulaOperator.TO_TEXT,
  FormulaOperator.TO_NUMBER,
  FormulaOperator.TO_DATE,
]
export type RowFormulaOperator = (typeof rowFormulaOperators)[number]

export const roundingFormulaOperators = [
  FormulaOperator.ROUND,
  FormulaOperator.CEIL,
  FormulaOperator.FLOOR,
]
export type RoundingFormulaOperator = (typeof roundingFormulaOperators)[number]

/**
 * Returns whether the operator is a column-level formula operator
 */
export function isColumnFormulaOperator(
  operator: FormulaOperator,
): operator is ColumnFormulaOperator {
  return _.includes(columnFormulaOperators, operator)
}

/**
 * Returns whether the operator is a row operator
 */
export function isRowFormulaOperator(operator: FormulaOperator): operator is RowFormulaOperator {
  return _.includes(rowFormulaOperators, operator)
}

/**
 * Returns whether the operator is a rounding operator
 */
export function isRoundingFormulaOperator(
  operator: FormulaOperator,
): operator is RoundingFormulaOperator {
  return _.includes(roundingFormulaOperators, operator)
}

/**
 * Checks if the input object is of type formula json
 */
export function isFormulaJson(obj: unknown): obj is FormulaJson {
  return yup
    .object({
      id: yup.string().uuid().required(),
      formulaValues: yup.array(),
    })
    .isValidSync(obj)
}

/**
 * Checks if the input object is of type formula value json
 */
export function isFormulaValueJson(obj: unknown): obj is FormulaJson {
  return yup
    .object({
      id: yup.string().uuid().required(),
      conditions: yup.array(),
      tableColumn: yup.mixed().optional().nullable(),
    })
    .isValidSync(obj)
}

/**
 * Checks if the input object is of type formula condition json
 */
export function isFormulaConditionJson(obj: unknown): obj is FormulaJson {
  return yup
    .object({
      id: yup.string().uuid().required(),
      condition: yup.mixed().required(),
    })
    .isValidSync(obj)
}

/**
 * Returns the formula keyword to use for SUM, COUNT, MIN, MAX, and rounding operators
 * All other operators use their associated symbol
 */
export function getFormulaKeyword(
  operator: FormulaOperator,
  hasColumnOperationFilter?: boolean,
): string {
  const spec = getFormulaOperatorSpec(operator)
  if (!spec) {
    throw new DossError('Formula keyword not supported for operator: %s', [operator])
  }
  return hasColumnOperationFilter && spec.filteredKeyword ? spec.filteredKeyword : operator
}
